import { useQuery } from "@tanstack/react-query"
import { Link, useParams, useRouter } from "@tanstack/react-router"
import { useEffect } from "react"
import { pickWorkspace, readLastWorkspace } from "@/lib/last-workspace"
import { workspacesQuery } from "@/lib/queries"
import { Logo } from "./logo"
import { NotFound } from "./not-found"
import { Card, CardDescription, CardTitle } from "./ui/card"

/**
 * `/w/$ws` for a workspace you don't belong to (earlier spec §1.2, UX §2.3): a centred card with a way out. A
 * workspace you do belong to only lands here for an unknown page inside it, which is a plain "not found".
 */
export function WorkspaceNoAccess() {
  const params = useParams({ strict: false }) as { workspaceId?: string }
  const list = useQuery(workspacesQuery).data
  const router = useRouter()
  const member = list?.workspaces.some((w) => w.id === params.workspaceId) ?? false
  // The list caught up (a workspace just joined): run the guards again instead of showing "no access".
  useEffect(() => {
    if (member) void router.invalidate()
  }, [member, router])
  if (member) return <NotFound />
  const others = { workspaces: list?.workspaces ?? [], activeWorkspaceId: list?.activeWorkspaceId ?? null }
  const pick = pickWorkspace(others, readLastWorkspace())
  const target = list?.workspaces.find((w) => w.id === pick)
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4 py-10">
      <Logo />
      <Card className="w-full max-w-md">
        <CardTitle>No access to this workspace</CardTitle>
        <CardDescription>
          You don't have access to this workspace. It may have been deleted, or you were removed.
        </CardDescription>
        <div className="mt-5">
          {target ? (
            <Link
              to="/w/$workspaceId"
              params={{ workspaceId: target.id }}
              className="inline-flex h-10 items-center rounded-control bg-brand px-4 text-sm font-semibold text-brand-ink no-underline hover:bg-brand-deep hover:text-brand-ink"
            >
              Go to {target.name}
            </Link>
          ) : (
            <Link
              to="/welcome"
              className="inline-flex h-10 items-center rounded-control bg-brand px-4 text-sm font-semibold text-brand-ink no-underline hover:bg-brand-deep hover:text-brand-ink"
            >
              Create a workspace
            </Link>
          )}
        </div>
      </Card>
    </main>
  )
}
